import React, { useMemo, useState } from "react";
import { Button, Col, Row, Spinner } from "react-bootstrap";
import { HeaderSection } from "../HeaderSection";
import { Model } from "../Model";
import { Title } from "../Title";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import styled from "styled-components";
import Calendar, { DateClickResult } from "../../../organisms/Calendar";
import {
  dateEquals,
  haceUnAno,
  obtenerNoches,
  obtenerStringDeFecha,
  stringADate,
  unAnoMas,
} from "../../../../utils/dateUtils";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DatosTransporte from "../DatosTransporte/DatosTransporte";
import Resumen from "../Resumen/Resumen";
import { useWizard } from "../../../../hooks/wizard";
import SuccessButton from "../../../organisms/SuccessButton";
import DatosBasicos from "../DatosBasicos/DatosBasicos";
import { usePasoReservaHotel } from "../pasoReservaHotel";
import {
  AsistenciaEnRango,
  useListarAsistenciasEnRango,
  useObtenerAsistenciaPorId,
} from "../../../../services/asistenciaServicios";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import ReagendamientoGuarderiaModal from "../../../reagendamiento/ReagendamientoGuarderiaModal";

const AVISO_ASISTENCIA_YA_REGISTRADA =
  "La asistencia de guardería de esta fecha ya fue registrada.";

function Fechas({ model }: { model: Model }) {
  const { avanza, vuelve, volver, avanzar } = useWizard();
  const { currentModel, definirFechas } = usePasoReservaHotel(model);

  const [asistenciaSeleccionada, setAsistenciaSeleccionada] = useState<{
    idAsistencia: string;
    fecha: Date;
    seleccionadaEn: number;
  }>();
  const [avisoGuarderia, setAvisoGuarderia] = useState<string>();

  const inicioRangoAsistencias = haceUnAno();
  const finRangoAsistencias = unAnoMas();

  const {
    data: asistencias,
    isLoading: asistenciasHaceUnAnoIsLoading,
    isError: asistenciasHaceUnAnoIsError,
  } = useListarAsistenciasEnRango(
    inicioRangoAsistencias,
    finRangoAsistencias,
    currentModel.idMascota
  );

  const {
    data: detalleAsistencia,
    isError: detalleAsistenciaIsError,
    dataUpdatedAt: detalleAsistenciaUpdatedAt,
  } = useObtenerAsistenciaPorId(
    asistenciaSeleccionada?.idAsistencia || "",
    !!asistenciaSeleccionada
  );

  const { fechasReservadasAnteriormente, asistenciasGuarderia } =
    useMemo(() => {
      const fechasHotel: Date[] = [];
      const guarderias: AsistenciaEnRango[] = [];
      (asistencias || []).forEach((asistencia) => {
        if (asistencia.tipoServicio === "HOTEL") {
          fechasHotel.push(stringADate(asistencia.fecha));
        }
        if (asistencia.tipoServicio === "GUARDERIA") {
          guarderias.push(asistencia);
        }
      });
      return {
        fechasReservadasAnteriormente: fechasHotel,
        asistenciasGuarderia: guarderias,
      };
    }, [asistencias]);

  const fechasReservadasGuarderia = useMemo(
    () =>
      asistenciasGuarderia.map((asistencia) => stringADate(asistencia.fecha)),
    [asistenciasGuarderia]
  );

  const nochesMaximas =
    currentModel.numeroNochesIndividuales +
    (currentModel.tarifa?.maximoNoches || 0);

  const nochesMinimas = currentModel.tarifa?.minimoNoches || 0;
  const nochesAgendadas = currentModel.fechasProgramadas.length;

  const nochesRestantes = nochesMaximas - nochesAgendadas;
  const canAddMoreDates = nochesRestantes > 0;

  const nochesFueronSeleccionadas =
    nochesMaximas >= nochesAgendadas &&
    nochesMinimas <= nochesAgendadas &&
    nochesAgendadas > 0;

  const puedeAvanzar =
    nochesFueronSeleccionadas && !asistenciasHaceUnAnoIsLoading;

  const isError = asistenciasHaceUnAnoIsError;

  const onDateClick = (date: Date): DateClickResult => {
    const asistenciaGuarderia = asistenciasGuarderia.find((asistencia) =>
      dateEquals(stringADate(asistencia.fecha), date)
    );

    if (asistenciaGuarderia) {
      if (asistenciaGuarderia.asistio) {
        setAsistenciaSeleccionada(undefined);
        setAvisoGuarderia(AVISO_ASISTENCIA_YA_REGISTRADA);
      } else {
        setAsistenciaSeleccionada({
          idAsistencia: asistenciaGuarderia.id,
          fecha: date,
          seleccionadaEn: Date.now(),
        });
        setAvisoGuarderia(undefined);
      }
      return { skipSelection: true };
    }

    setAsistenciaSeleccionada(undefined);
    setAvisoGuarderia(undefined);

    // En el calendario se seleccionan dias pero las reservas de hotel trabajan con noches.
    // Por eso se guardan por separado y las noches se calculan a partir de los dias (usando intervalos).
    let dias = currentModel.diasSeleccionados;
    const exists = dias.some((f) => f.getTime() === date.getTime());

    let diasActualizados: Date[] = [];

    if (exists) {
      diasActualizados = dias.filter((f) => f.getTime() !== date.getTime());
    } else {
      diasActualizados = [...dias, date];
    }

    const noches = obtenerNoches(
      diasActualizados.map((d) => obtenerStringDeFecha(d))
    );

    definirFechas({ dias: diasActualizados, noches });
  };

  if (avanza) {
    if (currentModel.contrataTransporte) {
      return <DatosTransporte model={currentModel} />;
    } else {
      return <Resumen model={currentModel} />;
    }
  }

  if (vuelve) {
    return <DatosBasicos model={currentModel} />;
  }

  if (isError) {
    return <ServerErrorModal />;
  }

  const detalleAsistenciaConfirmado =
    !!asistenciaSeleccionada &&
    !!detalleAsistencia &&
    detalleAsistencia.id === asistenciaSeleccionada.idAsistencia &&
    detalleAsistenciaUpdatedAt >= asistenciaSeleccionada.seleccionadaEn;

  const puedeReagendar =
    detalleAsistenciaConfirmado &&
    detalleAsistencia?.tipoServicio === "GUARDERIA" &&
    detalleAsistencia?.asistio === false;

  const asistenciaYaRegistrada =
    !!avisoGuarderia ||
    (detalleAsistenciaConfirmado && !!detalleAsistencia?.asistio);

  const cargandoDetalleAsistencia =
    !!asistenciaSeleccionada &&
    !detalleAsistenciaConfirmado &&
    !detalleAsistenciaIsError;

  return (
    <>
      {detalleAsistenciaIsError && asistenciaSeleccionada && (
        <ServerErrorModal
          onClose={() => setAsistenciaSeleccionada(undefined)}
        />
      )}
      {puedeReagendar && asistenciaSeleccionada && detalleAsistencia && (
        <ReagendamientoGuarderiaModal
          itemAsistencia={{
            idMascota: currentModel.idMascota,
            nombreMascota: currentModel.nombreMascota,
            nombrePropietario: currentModel.nombresPropietario,
          }}
          fecha={asistenciaSeleccionada.fecha}
          idReserva={detalleAsistencia.idReserva}
          fechasOcupadasAdicionales={currentModel.diasSeleccionados}
          onClose={() => setAsistenciaSeleccionada(undefined)}
        />
      )}
      <Row className="justify-content-center">
        <Col className="col-10">
          <HeaderSection className="pt-2 pb-2">
            <Title>
              Fechas de reserva
              <IconContainer>
                <CalendarMonthIcon sx={{ fontSize: 28 }} />
              </IconContainer>
            </Title>
          </HeaderSection>
          {asistenciasHaceUnAnoIsLoading && <div>Cargando calendario...</div>}
          {!asistenciasHaceUnAnoIsLoading && (
            <Row className="mb-3">
              <div className="mb-2">{`Mínimo noches: ${nochesMinimas}`}</div>
              <div className="mb-2">{`Máximo noches: ${nochesMaximas}`}</div>
              <div className="mb-2">{`Noches agendadas: ${nochesAgendadas}`}</div>
              <label className="mb-2">Elija fechas de agendamiento:</label>
              <Col className="col-12" style={{ paddingRight: "0" }}>
                <StyledCalendar
                  onDateClick={onDateClick}
                  canAddMoreDates={canAddMoreDates}
                  disabledDates={fechasReservadasAnteriormente}
                  dangerDates={fechasReservadasGuarderia}
                  preSelectedDates={currentModel.diasSeleccionados}
                />
              </Col>
              <label className="mb-2 text-muted">
                Nota: Las fechas en rojo son días reservados en guardería.
              </label>
              {asistenciaYaRegistrada && (
                <div className="mb-2 text-muted" role="status">
                  {avisoGuarderia || AVISO_ASISTENCIA_YA_REGISTRADA}
                </div>
              )}
              {cargandoDetalleAsistencia && (
                <div
                  className="mb-2 d-flex align-items-center gap-2"
                  role="status"
                >
                  <Spinner animation="border" size="sm" />
                  <span>Cargando información de la asistencia...</span>
                </div>
              )}
            </Row>
          )}

          <Row className="mb-2 justify-content-between">
            <Button
              type={"button"}
              style={{ width: "fit-content" }}
              onClick={volver}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
              Volver
            </Button>
            <SuccessButton
              type={"button"}
              style={{ width: "fit-content" }}
              disabled={!puedeAvanzar}
              onClick={avanzar}
            >
              Siguiente <ArrowForwardIcon sx={{ fontSize: 20 }} />
            </SuccessButton>
          </Row>
        </Col>
      </Row>
    </>
  );
}

const IconContainer = styled("span")`
  display: relative;
  margin-left: 8px;
  color: ${(props) => props.theme.colors.main};
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  border-radius: 5px;
  padding: 0%;
`;

export default Fechas;
